import React, { useState, useEffect } from "react";
import { FileText, RefreshCw, ChevronLeft, ChevronRight, CheckSquare, Square, Filter } from "lucide-react";
import { useTranslation } from "../../i18n/hooks/useTranslation";
import { ClientDetailsModal } from "../ClientDetailsModal";
import { formatDate as formatDateUtil } from "../../utils/dateUtils";
import { Invoice } from "../../types";
import { getInvoiceStatusColor } from "../../utils/statusColors";
import { BulkActionBar } from "../common/BulkActionBar";
import { useToast } from "../../contexts/ToastContext";
import { IntegrationSourceBadge } from "./IntegrationSourceBadge";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ProjectBadge } from "../common/ProjectBadge";

export const Invoices: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedClient, setSelectedClient] = useState<{
    id?: string;
    name: string;
  } | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const [showUnmappedOnly, setShowUnmappedOnly] = useState(false);
  const [isMapping, setIsMapping] = useState(false);
  const [updatingInvoiceId, setUpdatingInvoiceId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const pageSize = 20;

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    fetchInvoices();
  }, [currentPage, showUnmappedOnly]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");

      const unmappedParam = showUnmappedOnly ? "&unmapped=true" : "";
      const response = await fetch(
        `${apiUrl}/api/invoices?page=${currentPage}&limit=${pageSize}${unmappedParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      const data = await response.json();

      if (data.success) {
        setInvoices(data.data);
        setTotalCount(data.pagination?.total || data.data.length);
        setTotalPages(data.pagination?.totalPages || 1);
        // Clear selections when filters change
        setSelectedInvoiceIds(new Set());
      } else {
        setError(data.message || "Failed to fetch invoices");
      }
    } catch (err) {
      console.error("Fetch invoices error:", err);
      setError("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleSelect = (invoiceId: string) => {
    const newSelected = new Set(selectedInvoiceIds);
    if (newSelected.has(invoiceId)) {
      newSelected.delete(invoiceId);
    } else {
      newSelected.add(invoiceId);
    }
    setSelectedInvoiceIds(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedInvoiceIds.size === invoices.length) {
      setSelectedInvoiceIds(new Set());
    } else {
      setSelectedInvoiceIds(new Set(invoices.map((inv) => inv._id)));
    }
  };

  const handleMapToProject = async (projectId: string | null) => {
    if (selectedInvoiceIds.size === 0) return;

    try {
      setIsMapping(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/invoices/bulk-project`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoiceIds: Array.from(selectedInvoiceIds),
          projectId: projectId,
        }),
      });

      const data = await response.json();

      if (data.success) {
        const count = selectedInvoiceIds.size;
        setSelectedInvoiceIds(new Set());
        await fetchInvoices();
        showToast(
          `Successfully mapped ${count} ${count === 1 ? "invoice" : "invoices"} to project`,
          "success"
        );
      } else {
        showToast(data.message || "Failed to map invoices", "error");
      }
    } catch (err) {
      console.error("Map invoices error:", err);
      showToast("Failed to connect to server", "error");
    } finally {
      setIsMapping(false);
    }
  };

  const handleClearMapping = async () => {
    await handleMapToProject(null);
  };

  const handleBulkDelete = async () => {
    if (selectedInvoiceIds.size === 0) return;

    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");
      let successCount = 0;
      let failCount = 0;

      // Delete invoices one by one (backend doesn't have bulk delete endpoint)
      for (const invoiceId of Array.from(selectedInvoiceIds)) {
        try {
          const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          failCount++;
        }
      }

      setSelectedInvoiceIds(new Set());
      await fetchInvoices();

      if (failCount === 0) {
        showToast(`Successfully deleted ${successCount} ${successCount === 1 ? "invoice" : "invoices"}`, "success");
      } else {
        showToast(`Deleted ${successCount} invoices. ${failCount} failed.`, failCount > successCount ? "error" : "info");
      }
    } catch (error) {
      console.error("Bulk delete error:", error);
      showToast("Failed to delete invoices", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!invoiceToDelete) return;

    // Handle bulk delete
    if (invoiceToDelete === 'bulk') {
      await handleBulkDelete();
      setShowDeleteConfirm(false);
      setInvoiceToDelete(null);
      return;
    }

    // Handle single delete
    try {
      setIsDeleting(true);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/invoices/${invoiceToDelete}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        showToast("Invoice deleted successfully", "success");
        setShowDeleteConfirm(false);
        setInvoiceToDelete(null);
        await fetchInvoices();
      } else {
        showToast(data.message || "Failed to delete invoice", "error");
      }
    } catch (error) {
      console.error("Delete invoice error:", error);
      showToast("Failed to delete invoice", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const canDeleteInvoice = (invoice: Invoice) => {
    // Can only delete DRAFT, SUBMITTED, or VOIDED invoices (not APPROVED or PAID)
    return invoice.status === "DRAFT" || invoice.status === "SUBMITTED" || invoice.status === "VOIDED";
  };

  const handleIndividualProjectChange = async (invoiceId: string, projectId: string | null) => {
    try {
      setUpdatingInvoiceId(invoiceId);
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/invoices/${invoiceId}/project`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId }),
      });

      const data = await response.json();

      if (data.success) {
        await fetchInvoices();
        showToast(
          projectId ? "Invoice mapped to project successfully" : "Project mapping cleared",
          "success"
        );
      } else {
        showToast(data.message || "Failed to update invoice project mapping", "error");
      }
    } catch (err) {
      console.error("Update invoice project mapping error:", err);
      showToast("Failed to connect to server", "error");
    } finally {
      setUpdatingInvoiceId(null);
    }
  };

  const formatCurrency = (amount: number, currencyCode?: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
    }).format(amount);
  };

  const formatDate = (date?: Date | string) => {
    if (!date) return t("admin.invoices.notAvailable");
    return formatDateUtil(date);
  };

  // Use centralized status colors
  const getStatusBadgeColor = getInvoiceStatusColor;

  const getIntegrationSource = (invoice: Invoice) => {
    if (invoice.syncedFrom) {
      return invoice.syncedFrom;
    }
    // Fallback to legacy fields
    if (invoice.syncedFromXero) return "xero";
    if (invoice.syncedFromMYOB) return "myob";
    if (invoice.syncedFromQuickBooks) return "quickbooks";
    if (invoice.syncedFromZohoBooks) return "zohobooks";
    return null;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="w-12 h-12 rounded-lg bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50 flex items-center justify-center mr-4">
            <FileText className="w-6 h-6 text-accent-600 dark:text-accent-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t("admin.invoices.title")}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t("admin.invoices.description").replace(
                "{count}",
                totalCount.toString(),
              )}
            </p>
          </div>
        </div>

        <button
          onClick={fetchInvoices}
          disabled={loading}
          className="flex items-center px-4 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
        >
          <RefreshCw
            className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
          />
          {t("admin.invoices.refresh")}
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {/* Filter Toggle - Always visible (except when loading first page) */}
      {!(loading && currentPage === 1) && (
        <div className="mb-4 flex items-center justify-between">
          <button
            onClick={() => setShowUnmappedOnly(!showUnmappedOnly)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              showUnmappedOnly
                ? "bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 border border-accent-200 dark:border-accent-800"
                : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Show only unmapped</span>
            {showUnmappedOnly && totalCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-accent-600 dark:bg-accent-500 text-white text-xs rounded-full">
                {totalCount}
              </span>
            )}
          </button>
        </div>
      )}

      {loading && currentPage === 1 ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t("admin.invoices.loading")}
          </p>
        </div>
      ) : invoices.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-base font-medium mb-1">
                {showUnmappedOnly ? "No unmapped invoices found" : t("admin.invoices.noInvoices")}
              </p>
              <p className="text-sm mt-1">
                {showUnmappedOnly ? (
                  <>
                    All invoices are mapped to projects.{" "}
                    <button
                      onClick={() => setShowUnmappedOnly(false)}
                      className="text-accent-600 dark:text-accent-400 hover:underline font-medium"
                    >
                      Show all invoices
                    </button>
                  </>
                ) : (
                  t("admin.invoices.syncHint") ||
                  "Sync from your financial integration to view invoices"
                )}
              </p>
            </div>
          ) : (
            <>
            {/* Bulk Action Bar */}
            <BulkActionBar
              selectedCount={selectedInvoiceIds.size}
              onMapToProject={handleMapToProject}
              onClearMapping={handleClearMapping}
              onDeselectAll={() => setSelectedInvoiceIds(new Set())}
              onBulkDelete={() => {
                if (selectedInvoiceIds.size > 0) {
                  setInvoiceToDelete('bulk');
                  setShowDeleteConfirm(true);
                }
              }}
              isLoading={isMapping || isDeleting}
            />

            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider w-12">
                    <button
                      onClick={handleSelectAll}
                      className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
                      aria-label="Select all invoices"
                    >
                      {selectedInvoiceIds.size === invoices.length && invoices.length > 0 ? (
                        <CheckSquare className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                      ) : (
                        <Square className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                      )}
                    </button>
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.invoiceNumber")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.client")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.date")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.dueDate")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.status")}
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="text-left py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="text-right py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.total")}
                  </th>
                  <th className="text-right py-3.5 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.invoices.tableHeaders.amountDue")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  return (
                    <tr
                      key={invoice._id}
                      className={`border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                        selectedInvoiceIds.has(invoice._id)
                          ? "bg-accent-50/50 dark:bg-accent-900/10"
                          : ""
                      }`}
                    >
                      <td className="py-4 px-4">
                        <button
                          onClick={() => handleToggleSelect(invoice._id)}
                          className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors group"
                          aria-label="Select invoice"
                        >
                          {selectedInvoiceIds.has(invoice._id) ? (
                            <CheckSquare className="w-4 h-4 text-accent-600 dark:text-accent-400" />
                          ) : (
                            <Square className="w-4 h-4 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                          )}
                        </button>
                      </td>
                      <td className="py-4 px-4 text-sm font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="py-4 px-4 text-sm whitespace-nowrap">
                        {typeof invoice.clientId === "object" &&
                        invoice.clientId &&
                        invoice.clientId.name ? (
                          <button
                            onClick={() => {
                              const client = invoice.clientId as {
                                _id?: string;
                                name: string;
                              };
                              setSelectedClient({
                                id: client._id,
                                name: client.name,
                              });
                            }}
                            className="text-accent-600 dark:text-accent-400 hover:text-accent-800 dark:hover:text-accent-300 hover:underline cursor-pointer"
                          >
                            {invoice.clientId.name}
                          </button>
                        ) : (
                          <span className="text-gray-900 dark:text-gray-100">
                            {t("admin.invoices.notAvailable")}
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        {formatDate(invoice.dueDate)}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded ${getStatusBadgeColor(invoice.status)}`}
                        >
                          {invoice.status}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        <IntegrationSourceBadge source={getIntegrationSource(invoice)} />
                      </td>
                      <td className="py-4 px-4 text-sm whitespace-nowrap">
                        <ProjectBadge
                          projectId={typeof invoice.projectId === 'object' && invoice.projectId ? invoice.projectId._id : invoice.projectId as string | null}
                          projectName={typeof invoice.projectId === 'object' && invoice.projectId ? `${invoice.projectId.projectNumber}${invoice.projectId.name ? ` - ${invoice.projectId.name}` : ''}` : undefined}
                          onChange={(projectId) => handleIndividualProjectChange(invoice._id, projectId)}
                          disabled={updatingInvoiceId === invoice._id}
                        />
                      </td>
                      <td className="py-4 px-4 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(invoice.total, invoice.currencyCode)}
                      </td>
                      <td className="py-4 px-4 text-sm text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatCurrency(
                          invoice.amountDue || 0,
                          invoice.currencyCode,
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

            {/* Pagination */}
            {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                {t("admin.invoices.pagination")
                  .replace("{current}", currentPage.toString())
                  .replace("{total}", totalPages.toString())
                  .replace("{count}", totalCount.toString())}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1 || loading}
                  className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  {t("admin.invoices.previous")}
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages || loading}
                  className="flex items-center px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {t("admin.invoices.next")}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
            </>
          )}

      {/* Client Details Modal */}
      {selectedClient && (
        <ClientDetailsModal
          isOpen={!!selectedClient}
          onClose={() => setSelectedClient(null)}
          clientId={selectedClient.id}
          clientName={selectedClient.name}
          isSupplier={false}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title={invoiceToDelete === 'bulk' ? "Delete Invoices" : "Delete Invoice"}
        message={
          invoiceToDelete === 'bulk'
            ? `Are you sure you want to delete ${selectedInvoiceIds.size} ${selectedInvoiceIds.size === 1 ? "invoice" : "invoices"}? This action cannot be undone. Only DRAFT, SUBMITTED, or VOIDED invoices can be deleted.`
            : "Are you sure you want to delete this invoice? This action cannot be undone."
        }
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setInvoiceToDelete(null);
        }}
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        type="danger"
      />
    </div>
  );
};
